import { Box, createStyles, Input, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { FocusEvent, KeyboardEvent, ReactNode, RefObject, SyntheticEvent, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ErrorHelperText, Space } from '@/components';
import { Func } from '@/utils/ts';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    error: {
      alignSelf: 'center',
      paddingTop: theme.spacing(1),
    },
    codeBox: {
      justifyContent: 'center',
      display: 'flex',
      '& > *': {
        marginRight: '3px',
        marginLeft: '3px',
      },
      '& > :first-child': {
        marginLeft: 0,
      },
      '& > :last-child': {
        marginRight: 0,
      },
      '& > :nth-child(3)': {
        marginRight: '10px',
      },
      '& > :nth-child(4)': {
        marginLeft: '10px',
      },
    },
    codeBoxError: {
      marginBottom: '0 !important',
    },
    codeGroupBox: {
      marginRight: '7px',
      marginLeft: '7px',
      flex: 'auto',
      '& > *': {
        marginRight: '3px',
        marginLeft: '3px',
      },
    },
    singleDigitInput: {
      width: '40px',
      marginBottom: 0,
    },
    singleDigitInputHTML: {
      lineHeight: '51px',
      height: '51px',
      padding: '2.5px',
      textAlign: 'center',
      fontSize: '30px',
      borderRadius: '8px',
      width: '40px',
    },
  }),
);

interface DigitInputProps {
  value?: string;
  ref?: React.Ref<HTMLInputElement>;
  hasError?: boolean;
  onBackspace: Func;
  onChange: Func<[string]>;
}

const DigitInput: React.FC<DigitInputProps> = React.forwardRef(
  ({ value, hasError, onChange, onBackspace }: DigitInputProps, ref) => {
    const classes = useStyles();
    return (
      <Input
        autoComplete="off"
        type="number"
        inputRef={ref}
        value={value?.trim()}
        // because real event is in e, nativeEvent differs for browsers
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onBeforeInput={(e: InputEvent): void => {
          e.preventDefault();
          const data = e.data?.replace(/[^\d]/g, '');
          if (data?.length && data?.length > 0) {
            const newValue = data.charAt(0);
            if (e.target) {
              e.target.dispatchEvent(new InputEvent('input', { bubbles: true, data: newValue }));
            }
          }
        }}
        onInput={(e: SyntheticEvent<HTMLInputElement, InputEvent>): void => {
          const newValue = e.nativeEvent?.data || '';
          onChange(newValue);
        }}
        onKeyUp={(event: KeyboardEvent<HTMLInputElement>): void => {
          // 8 - backspace
          if (event.keyCode === 8 && value?.replace(/[^\d]/g, '') === '') {
            onBackspace();
          }
        }}
        onFocus={(event: FocusEvent<HTMLInputElement>): void => {
          event.target.select();
        }}
        disableUnderline
        className={clsx('ym-hide-content', classes.singleDigitInput)}
        inputProps={{ className: clsx(classes.singleDigitInputHTML), inputMode: 'numeric' }}
        {...(hasError
          ? {
              error: true,
              'aria-describedby': 'component-error-text',
            }
          : {})}
      />
    );
  },
);

interface ConfirmationCodeInputProps {
  value?: string;
  errorId?: string;
  onChange: Func<[string]>;
}

const ConfirmationCode: React.FC<ConfirmationCodeInputProps> = ({
  value: initialValue,
  onChange,
  errorId,
}: ConfirmationCodeInputProps) => {
  const classes = useStyles();
  const [initial, saveInitial] = useState<string | undefined>();
  const [value, setValue] = useState<string[]>((initialValue || '').padEnd(6, ' ').split(''));

  useEffect(() => {
    if (initialValue !== initial) {
      saveInitial(initialValue);
      setValue((initialValue || '').padEnd(6, ' ').split(''));
    }
  }, [initial, initialValue]);
  const handleChangeValue = (newValue: string | undefined, idx: number): void => {
    if (idx < value.length) {
      const v = [...value];
      v[idx] = newValue || ' ';
      setValue(v);
      onChange(v.join(''));
    }
  };
  const components: Array<[ReactNode, RefObject<HTMLInputElement>]> = Array.from({ length: 6 }, (v, i) => i).map(
    (idx: number) => {
      const ref = React.createRef<HTMLInputElement>();
      return [
        <DigitInput
          key={`digitalinput_${idx}`}
          hasError={!!errorId}
          value={value[idx]}
          ref={ref}
          onBackspace={(): void => {
            if (idx - 1 >= 0) {
              components[idx - 1]?.[1]?.current?.focus();
            }
          }}
          onChange={(newValue: string | undefined): void => {
            handleChangeValue(newValue, idx);
            if (newValue?.trim() !== '') {
              if (idx + 1 < components.length) {
                components[idx + 1]?.[1]?.current?.focus();
              }
            }
          }}
        />,
        ref,
      ];
    },
  );

  return (
    <Space direction="vertical" size={0} style={{ alignItems: 'center' }}>
      <Box className={clsx(classes.codeBox, errorId && classes.codeBoxError)}>
        {components.map(([component]) => component)}
      </Box>
      {!!errorId && (
        <ErrorHelperText className={classes.error}>
          <FormattedMessage id={errorId} />
        </ErrorHelperText>
      )}
    </Space>
  );
};

export default ConfirmationCode;
